/**
 * TEMP-H-011: Server-based Iteration API Service
 * 
 * Replaces direct Supabase queries with server API calls for better security
 * and data contract consistency. All operations go through /api/executions/** endpoints.
 */

import { API_ENDPOINTS } from '@/lib/api-base';
import { apiRequest } from '@/lib/api-client';
import { StageType } from '@/types/execution';

export interface SelectIterationResponse {
  success: boolean;
  invalidated_stages: StageType[];
  message?: string;
}

export interface RetryStageResponse {
  success: boolean;
  new_iteration_id: string;
  message?: string;
}

export interface ControlExecutionResponse {
  success: boolean;
  new_status: string;
  message?: string;
}

export interface ExecutionDetailsResponse {
  execution: any;
  stages: any[];
  iterations: Record<StageType, any[]>;
  outputs: Record<StageType, any>;
  total_cost: number;
}


export class ServerIterationAPIService {
  /**
   * Select an iteration for a specific stage via server API
   */
  static async selectIteration(
    executionId: string, 
    stage: StageType, 
    iterationId: string
  ): Promise<SelectIterationResponse> {
    try {
      const response = await apiRequest<SelectIterationResponse>(
        `${API_ENDPOINTS.executions.iterations(executionId)}/select`,
        {
          method: 'POST',
          body: JSON.stringify({
            stage,
            iteration_id: iterationId,
          }),
          includeAuth: true,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to select iteration:', error);
      const message = error instanceof Error ? error.message : 'Failed to select iteration';
      
      return {
        success: false,
        invalidated_stages: [],
        message
      };
    }
  }

  /**
   * Retry a stage by creating a new iteration via server API
   */
  static async retryStage(
    executionId: string, 
    stage: StageType, 
    options: { 
      max_iterations?: number; 
      threshold_override?: number; 
    } = {}
  ): Promise<RetryStageResponse> {
    try {
      const response = await apiRequest<RetryStageResponse>(
        `${API_ENDPOINTS.executions.stages(executionId)}/retry`,
        {
          method: 'POST',
          body: JSON.stringify({
            stage,
            options,
          }),
          includeAuth: true,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to retry stage:', error);
      const message = error instanceof Error ? error.message : 'Failed to retry stage';
      
      return {
        success: false,
        new_iteration_id: '',
        message
      };
    }
  }

  /**
   * Control pipeline execution (pause/resume/stop) via server API
   */
  static async controlExecution(
    executionId: string, 
    action: 'pause' | 'resume' | 'stop'
  ): Promise<ControlExecutionResponse> {
    try {
      const response = await apiRequest<ControlExecutionResponse>(
        `${API_ENDPOINTS.executions.update(executionId)}/control`,
        {
          method: 'POST',
          body: JSON.stringify({
            action,
          }),
          includeAuth: true,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to ${action} execution:`, error);
      const message = error instanceof Error ? error.message : `Failed to ${action} execution`;
      
      return {
        success: false,
        new_status: '',
        message
      };
    }
  }

  /**
   * Get execution details with iterations via server API
   */
  static async getExecutionDetails(executionId: string): Promise<ExecutionDetailsResponse> {
    try {
      const response = await apiRequest<ExecutionDetailsResponse>(
        API_ENDPOINTS.executions.get(executionId),
        {
          method: 'GET',
          includeAuth: true,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get execution details:', error);
      throw error;
    }
  }

  /**
   * Get all executions via server API
   */
  static async getExecutions(options: {
    limit?: number;
    offset?: number;
    status?: string;
  } = {}): Promise<{ executions: any[]; total: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.set('limit', options.limit.toString());
      if (options.offset) queryParams.set('offset', options.offset.toString());
      if (options.status) queryParams.set('status', options.status);

      const endpoint = options.limit || options.offset || options.status
        ? `${API_ENDPOINTS.executions.list()}?${queryParams.toString()}`
        : API_ENDPOINTS.executions.list();

      const response = await apiRequest<{ executions: any[]; pagination: { total: number } }>(
        endpoint,
        {
          method: 'GET',
          includeAuth: true,
        }
      );
      // Transform response to match expected format
      return {
        executions: response.executions,
        total: response.pagination.total
      };
    } catch (error) {
      console.error('Failed to get executions:', error);
      throw error;
    }
  }

  /**
   * Create new execution via server API
   */
  static async createExecution(config: {
    stages: StageType[];
    settings?: Record<string, any>;
  }): Promise<{ execution_id: string; message: string }> {
    try {
      // Transform frontend format to backend format
      const backendPayload = {
        title: config.settings?.title || 'New Pipeline Execution',
        description: config.settings?.description || 'Created from dashboard',
        stages: config.stages,
        max_budget_usd: config.settings?.max_budget_usd || 25.0,
        timeout_hours: config.settings?.timeout_hours || 2,
        execution_config: config.settings?.execution_config || {},
        content_config: config.settings?.content_config || {},
        creative_config: config.settings?.creative_config || {}
      };

      const response = await apiRequest<{ id: string; status: string; workflow_id: string }>(
        API_ENDPOINTS.executions.create(),
        {
          method: 'POST',
          body: JSON.stringify(backendPayload),
          includeAuth: true,
        }
      );

      // Transform response to match expected format
      return {
        execution_id: response.id,
        message: `Execution created with status: ${response.status}`
      };
    } catch (error) {
      console.error('Failed to create execution:', error);
      throw error;
    }
  }

  /**
   * Get execution events via server API (for timeline)
   */
  static async getExecutionEvents(
    executionId: string,
    options: {
      limit?: number;
      offset?: number;
      event_type?: string;
    } = {}
  ): Promise<{ events: any[]; total: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.set('limit', options.limit.toString());
      if (options.offset) queryParams.set('offset', options.offset.toString());
      if (options.event_type) queryParams.set('event_type', options.event_type);

      const endpoint = options.limit || options.offset || options.event_type
        ? `${API_ENDPOINTS.executions.events(executionId)}?${queryParams.toString()}`
        : API_ENDPOINTS.executions.events(executionId);

      const response = await apiRequest<{ events: any[]; total: number }>(
        endpoint,
        {
          method: 'GET',
          includeAuth: true,
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to get execution events:', error);
      throw error;
    }
  }

  /**
   * Get stage dependencies for invalidation logic via server API
   */
  static async getStageDependencies(stage: StageType): Promise<StageType[]> {
    try {
      const response = await apiRequest<{ dependencies: StageType[] }>(
        API_ENDPOINTS.stages.dependencies(stage),
        {
          method: 'GET',
          includeAuth: true,
        }
      );

      return response.dependencies || [];
    } catch (error) {
      console.error('Failed to get stage dependencies:', error);
      return [];
    }
  }

  /**
   * Calculate total cost for an execution (client-side utility)
   */
  static calculateTotalCost(iterations: any[]): number {
    return iterations.reduce((sum, iter) => sum + (iter.cost_usd || 0), 0);
  }
}

// Export convenience functions for backward compatibility
export const selectIteration = ServerIterationAPIService.selectIteration;
export const retryStage = ServerIterationAPIService.retryStage;
export const controlExecution = ServerIterationAPIService.controlExecution;
export const getExecutionDetails = ServerIterationAPIService.getExecutionDetails;
export const getExecutions = ServerIterationAPIService.getExecutions;
export const createExecution = ServerIterationAPIService.createExecution;
export const getExecutionEvents = ServerIterationAPIService.getExecutionEvents;
export const getStageDependencies = ServerIterationAPIService.getStageDependencies;